import { usePlayerStore } from '@/store/playerStore';
import { useConnectionStore } from '@/store/connectionStore';
import { X, Activity, Wifi, ShieldCheck, CheckCircle2, Sliders } from 'lucide-react';
import type { VideoQuality } from '@/types';

interface StreamTelemetryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onQualityChange?: (quality: VideoQuality) => void;
}

const RESOLUTIONS: { label: string; value: VideoQuality; badge: string; desc: string }[] = [
  { label: '1080p', value: '1080p', badge: 'Full HD (Fixed)', desc: 'Sharpest picture, locked resolution (8 Mbps)' },
  { label: '720p', value: '720p', badge: 'HD (Smooth)', desc: 'Smooth playback on moderate connections (3.5 Mbps)' },
  { label: '480p', value: '480p', badge: 'SD (Data Saver)', desc: 'Maximum stability for weak Wi-Fi or mobile data' },
  { label: 'Auto', value: 'auto', badge: 'Adaptive', desc: 'Browser automatically balances resolution and framerate' },
];

export function StreamTelemetryModal({ isOpen, onClose, onQualityChange }: StreamTelemetryModalProps) {
  const { stats, selectedQuality, setSelectedQuality } = usePlayerStore();
  const { isRelay } = useConnectionStore();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm font-sans animate-fade-in">
      <div className="bg-cinema-900 border border-cinema-700/80 rounded-xl max-w-md w-full p-6 shadow-2xl relative text-cinema-100 contain-overscroll">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-md text-cinema-400 hover:text-cinema-100 hover:bg-cinema-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
          aria-label="Close quality details"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2.5 mb-5 pb-3 hairline-b">
          <Activity className="w-5 h-5 text-lamp" />
          <div>
            <h3 className="text-sm font-bold text-cinema-100 tracking-tight">Stream Quality & Connection</h3>
            <p className="text-xs text-cinema-400">Live connection performance</p>
          </div>
        </div>

        {stats ? (
          <div className="space-y-4">
            {/* Grid of Key Metrics */}
            <div className="grid grid-cols-2 gap-2.5 text-xs">
              <div className="p-3 rounded-lg bg-cinema-950 border border-cinema-700/60">
                <div className="text-cinema-400 text-xs mb-1">Resolution</div>
                <div className="text-sm font-semibold text-cinema-100">{stats.resolution}</div>
              </div>

              <div className="p-3 rounded-lg bg-cinema-950 border border-cinema-700/60">
                <div className="text-cinema-400 text-xs mb-1">Bitrate</div>
                <div className="text-sm font-semibold text-cinema-100">
                  {stats.bitrate > 0 ? `${(stats.bitrate / 1000).toFixed(1)} Mbps` : 'Direct Source'}
                </div>
              </div>

              <div className="p-3 rounded-lg bg-cinema-950 border border-cinema-700/60">
                <div className="text-cinema-400 text-xs mb-1">Frame Rate</div>
                <div className="text-sm font-semibold text-cinema-100">
                  {stats.frameRate > 0 ? `${stats.frameRate} fps` : 'Synced'}
                </div>
              </div>

              <div className="p-3 rounded-lg bg-cinema-950 border border-cinema-700/60">
                <div className="text-cinema-400 text-xs mb-1">Latency / Ping</div>
                <div className="text-sm font-semibold text-cinema-100">{stats.roundTripTime} ms</div>
              </div>
            </div>

            {/* Connection Channel */}
            <div className="p-3 rounded-lg bg-cinema-950 border border-cinema-700/60 text-xs">
              <div className="flex items-center justify-between mb-1">
                <span className="text-cinema-400">Connection Mode</span>
                <span className={`font-medium ${isRelay ? 'text-amber-400' : 'text-reel-emerald'}`}>
                  {isRelay ? 'Relayed Connection' : 'Direct Peer-to-Peer'}
                </span>
              </div>
              <p className="text-xs text-cinema-400 leading-relaxed">
                {isRelay
                  ? 'Your network firewall requires a secure relay to connect with your peer.'
                  : 'Video frames are streaming directly between your device and your peer.'}
              </p>
            </div>

            {/* Manual Stream Resolution Control */}
            <div className="p-3 rounded-lg bg-cinema-950 border border-cinema-700/60 text-xs space-y-2">
              <div className="flex items-center justify-between pb-1 hairline-b">
                <span className="font-semibold text-cinema-200 uppercase text-[11px] tracking-wider flex items-center gap-1.5">
                  <Sliders className="w-3.5 h-3.5 text-lamp" />
                  <span>Manual Resolution Control</span>
                </span>
                <span className="text-lamp font-mono font-bold text-[11px]">{selectedQuality.toUpperCase()}</span>
              </div>
              <div className="grid grid-cols-2 gap-1.5 pt-1">
                {RESOLUTIONS.map((res) => (
                  <button
                    key={res.value}
                    onClick={() => {
                      setSelectedQuality(res.value);
                      onQualityChange?.(res.value);
                    }}
                    className={`p-2 rounded text-left transition-all border ${
                      selectedQuality === res.value
                        ? 'bg-cinema-800 border-lamp/60 text-cinema-100 shadow-sm'
                        : 'bg-cinema-900 border-cinema-800 text-cinema-300 hover:text-cinema-100 hover:border-cinema-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold">{res.label}</span>
                      <span className="text-[10px] text-cinema-400">{res.badge}</span>
                    </div>
                    <div className="text-[10px] text-cinema-500 truncate mt-0.5">{res.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6 text-center text-xs text-cinema-400 space-y-2">
            <Activity className="w-8 h-8 text-cinema-600 mx-auto animate-pulse" />
            <p>Gathering connection statistics…</p>
          </div>
        )}

        <div className="mt-5 pt-3 hairline-t text-right">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md bg-cinema-800 hover:bg-cinema-750 text-xs font-semibold text-cinema-200 hover:text-cinema-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
